import React, { createContext, useEffect } from "react";
import { useAuth0, User } from "@auth0/auth0-react";
import {
  setAuthToken,
  removeAuthToken,
  setupAxiosInterceptor,
  waitForAuthToken,
} from "../../api/axios";

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  error?: Error;
  user?: User;
  login: () => void;
  logout: () => void;
  getAccessToken: () => Promise<string>;
  changePassword: () => void;
}

interface AuthProviderType {
  children: React.ReactNode;
}

export const AuthContext = createContext<AuthContextType | undefined>(
  undefined
);

export const AuthProvider = ({ children }: AuthProviderType) => {
  const {
    isAuthenticated,
    isLoading,
    user,
    error,
    loginWithRedirect,
    logout: auth0Logout,
    getAccessTokenSilently,
  } = useAuth0();

  const handleLogout = () => {
    removeAuthToken();
    auth0Logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });
  };

  useEffect(() => {
    setupAxiosInterceptor(handleLogout);
    waitForAuthToken();
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      getAccessTokenSilently()
        .then((token) => setAuthToken(token))
        .catch(() => removeAuthToken());
    } else if (!isLoading) {
      removeAuthToken();
    }
  }, [isAuthenticated, isLoading, getAccessTokenSilently]);

  const handleLogin = () => {
    loginWithRedirect();
  };

  const getAccessToken = async (): Promise<string> => {
    return await getAccessTokenSilently();
  };

  const handleChangePassword = async () => {
    if (!user?.email) {
      alert('Unable to send password reset email: No email found');
      return;
    }

    try {
      const auth0Domain = process.env.REACT_APP_AUTH0_DOMAIN!;
      const clientId = process.env.REACT_APP_AUTH0_CLIENT_ID!;

      const response = await fetch(`https://${auth0Domain}/dbconnections/change_password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_id: clientId,
          email: user.email,
          connection: 'Username-Password-Authentication'
        })
      });

      if (response.ok) {
        alert('Password reset email sent! Please check your inbox.');
      } else {
        alert('Failed to send password reset email. Please try again.');
      }
    } catch (error) {
      alert('Failed to send password reset email. Please try again.');
    }
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        error,
        user,
        login: handleLogin,
        logout: handleLogout,
        getAccessToken,
        changePassword: handleChangePassword,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
